import dayjs from 'dayjs'
import {
  GeckoDRA,
  GeckoSubDRA,
  RautReportIndication,
  useIndicationSets,
} from 'features/indicationTriage'
import { PDFImageData } from 'features/reporting/pdfGeneration/types/pdfTypes'
import {
  BaseContextValue,
  SavedIndicationGraph,
  SavedWeldIndicationGraph,
  SectionSelection,
} from 'features/reporting/types'
import { useRolodexEntries } from 'features/rolodex'
import { useScanAreaRatio, useScanCount } from 'features/scans'
import isEmpty from 'lodash/isEmpty'
import { TYPE_TO_METHOD_TITLE } from 'pages/reporting/shared/constants'
import { useMetadataEntry } from 'pages/reporting/shared/hooks/useMetadataEntry'
import { usePdfManagement } from 'pages/reporting/shared/hooks/UsePDFManagement'
import { ReportTemplateId } from 'pages/reporting/shared/reportTemplates'
import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDefaults,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoInspectionType,
  GeckoMarkupLayer,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoSource,
  GeckoView,
  GeckoVisualInspection,
  RolodexEntryType,
  SignatureData,
  SignoffState,
} from 'types'

import { computeAssetDimensions } from './helpers/computeAssetDimensions'
import { AssetDrawings, useAssetDrawings } from './hooks/useAssetDrawings'
import { useAssetVizState } from './hooks/useAssetVizState'
import { useCalibrationImage } from './hooks/UseCalibrationImage'
import { useCantileverReportData } from './hooks/useCantileverReportData'
import { ClientImage, useClientImages } from './hooks/useClientImages'
import { getSelectedFormData, useFormData } from './hooks/useFormData'
import { useIndications } from './hooks/useIndications'
import { useIndicationsWithImages } from './hooks/useIndicationsWithImages'
import { useLockManagement } from './hooks/useLockManagement'
import { useReportNavigation } from './hooks/useReportNavigation'
import { useReportSubmission } from './hooks/useReportSubmission'
import { useTotalUT } from './hooks/useTotalUT'
import { useWeldIndications, WeldIndication } from './hooks/useWeldIndications'
import { useWeldIndicationsWithImages } from './hooks/useWeldIndicationsWithImages'
import { rautReportForSPA } from './rautReport'
import {
  CalibrationPageData,
  ClientProvidedImagesData,
  CoverPageData,
  MainInfo,
  RautFormDataState,
  RautInspectionAssetDrawingsData,
  SummaryPageData,
  WeldScanData,
} from './rautReportData'

export interface RautReportContextValue extends BaseContextValue {
  refreshMetadataEntry: () => Promise<void>
  refreshPdfState: () => Promise<void>

  // Form Data
  formData: RautFormDataState
  updateFormData: (newData: Partial<RautFormDataState>) => void
  handleSignoffFinish: (values: SignatureData) => Promise<SignoffState>
  revertFormToCantilever: () => Promise<void>
  handleMainInfoFinish: (values: MainInfo) => Promise<void>
  handleCoverPageFinish: (values: CoverPageData) => Promise<void>
  handleSummaryPageFinish: (values: SummaryPageData) => Promise<void>
  saveSummaryPageSilent: (values: SummaryPageData) => Promise<void>
  saveMainInfoSilent: (values: Partial<MainInfo>) => Promise<void>
  saveCoverPageSilent: (values: CoverPageData) => Promise<void>
  saveCalibrationSilent: (
    values: Partial<CalibrationPageData>,
    isComplete: boolean,
    deleteData?: string[]
  ) => Promise<void>
  handleCalibrationFinish: (
    values: CalibrationPageData,
    isComplete: boolean
  ) => Promise<void>
  handleClientProvidedImagesFinish: (
    values: ClientProvidedImagesData
  ) => Promise<void>
  saveClientProvidedImagesSilent: (
    values: ClientProvidedImagesData
  ) => Promise<void>
  handleAssetDrawingsFinish: (
    inspectionId: string,
    inspectionDrawings: RautInspectionAssetDrawingsData
  ) => Promise<void>
  handleWeldScanDataFinish: (values: WeldScanData) => Promise<void>
  handleSaveIndicationGraph: (
    graphData: SavedIndicationGraph
  ) => Promise<Record<string, SavedIndicationGraph>>
  handleDeleteIndicationGraph: (
    indicationId: string
  ) => Promise<Record<string, SavedIndicationGraph>>
  handleSaveWeldIndicationGraph: (
    indicationId: string,
    imagePath: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  handleDeleteWeldIndicationGraph: (
    indicationId: string
  ) => Promise<Record<string, SavedWeldIndicationGraph>>
  registerPageSaveHandler: (
    pageId: string,
    handler?: (() => Promise<void>) | null
  ) => void
  savePageBeforeNavigation: (page: string | null) => Promise<void>

  selectedRevisionData: RautFormDataState | undefined

  indicationsWithImages: RautReportIndication[]
  renderedImageUrls: Record<string, string>

  assetDrawings: ReadonlyMap<string, AssetDrawings>
  calibrationImage: PDFImageData | undefined
  clientImages: ClientImage[]

  // Cantilever Data
  hasCantileverData: boolean
  prePopulatedData: Partial<MainInfo & CoverPageData>

  // Inspection Data (Multiplexed for multiple inspections/components)
  inspections: readonly (GeckoInspection | GeckoVisualInspection)[]
  componentsByInspection: ReadonlyMap<string, GeckoComponent>
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>
  assetModel: GeckoAssetModel | undefined
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
  componentModels: readonly GeckoComponentModel[]
  relevantThicknessCDFsByInspection: ReadonlyMap<
    string,
    GeckoComponentDataFrame
  >
  flatIndications: RautReportIndication[]
  indicationsByInspectionId: ReadonlyMap<string, RautReportIndication[]>
  scanCount: ReadonlyMap<string, number>
  totalUT: number
  scanArea: number
  majorDamageRating: GeckoSubDRA | undefined
  uniqueScanTypes: string[]
  uniqueScanTypesString: string
  assetDiameter?: GeckoMeasurement
  assetLength?: GeckoMeasurement
  nominalsByInspection: ReadonlyMap<
    string,
    GeckoMeasurement | GeckoMeasurementRange
  >
  componentDefaults: readonly GeckoComponentDefaults[]

  // Weld Indications from Maxweld
  weldIndicationsByInspectionId: ReadonlyMap<string, WeldIndication[]>
  flatWeldIndications: WeldIndication[]
  weldIndicationsWithImages: WeldIndication[]
  renderedWeldImageUrls: Record<string, string>
  isLoadingWeldIndications: boolean
  hasWeldIndications: boolean
  hasPhasedArrayInspection: boolean
}

interface RautReportProviderProps {
  children: React.ReactNode
  reportId: string
  requestedMetadataEntryId: string | undefined
  siteId: string
}

export const RautReportContext = createContext<
  RautReportContextValue | undefined
>(undefined)

export const RautReportProvider: FC<RautReportProviderProps> = ({
  children,
  reportId,
  requestedMetadataEntryId,
  siteId,
}) => {
  const {
    metadataEntryId,
    metadataEntries,
    isLoading: isLoadingMetadata,
    publishRevision,
    revisions,
    currentRevisionNumber,
    createNewRevision,
    isCurrentRevisionPublished,
    createInitialEntry,
    refreshMetadataEntry,
  } = useMetadataEntry(reportId, siteId, requestedMetadataEntryId)

  const currentMetadataRevision = useMemo(() => {
    return metadataEntries.find((entry) => entry.id === metadataEntryId)
  }, [metadataEntries, metadataEntryId])

  // We currently only support one inspection per report, so get the first one
  const inspectionIds = useMemo(() => {
    return currentMetadataRevision?.data.inspections
  }, [currentMetadataRevision])

  const {
    inspections,
    units,
    getComponent,
    isLoading: isLoadingInspections,
  } = useRolodexEntries(
    inspectionIds
      ? [
          {
            type: RolodexEntryType.INSPECTION,
            ids: inspectionIds,
            links: [RolodexEntryType.COMPONENT, RolodexEntryType.UNIT],
          },
        ]
      : undefined
  )

  // We expect only one unit per report
  const unit = useMemo(() => units.at(0), [units])

  const componentsByInspection = useMemo(() => {
    const map = new Map<string, GeckoComponent>()
    for (const inspection of inspections) {
      const component = getComponent(inspection.tags.component)
      if (component) {
        map.set(inspection.id, component)
      }
    }
    return map
  }, [inspections, getComponent])

  const componentIds = useMemo(
    () => Array.from(componentsByInspection.values()).map((c) => c.id),
    [componentsByInspection]
  )

  const { componentDefaults } = useRolodexEntries(
    componentIds.length > 0
      ? [
          {
            type: RolodexEntryType.COMPONENT_DEFAULTS,
            tags: {
              [RolodexEntryType.COMPONENT]: componentIds,
            },
          },
        ]
      : undefined
  )

  const { markupLayers, isLoading: isLoadingMarkupLayers } = useRolodexEntries(
    componentsByInspection.size > 0 && inspections.length > 0
      ? [
          {
            type: RolodexEntryType.MARKUP_LAYER,
            tags: {
              component: componentIds,
            },
          },
        ]
      : undefined
  )

  const { assetModels, isLoading: isLoadingAssetModels } = useRolodexEntries(
    inspections.length > 0
      ? [
          {
            type: RolodexEntryType.ASSET_MODEL,
            tags: {
              unit: inspections.map((inspection) => inspection.tags.unit),
            },
          },
        ]
      : undefined
  )

  const {
    componentDatasets,
    componentDataFrames,
    isLoading: isLoadingComponentDatasets,
  } = useRolodexEntries(
    inspections.length > 0
      ? [
          {
            type: RolodexEntryType.COMPONENT_DATASET,
            tags: {
              unit: inspections.map((inspection) => inspection.tags.unit),
            },
          },
        ]
      : undefined
  )

  // should only be one asset model per report
  const assetModel = useMemo(() => {
    if (assetModels.length > 1) {
      throw Error(`More than 1 asset model found`)
    }
    return assetModels.at(0)
  }, [assetModels])

  const componentModelsByInspection = useMemo(() => {
    const map = new Map<string, GeckoComponentModel>()
    for (const inspection of inspections) {
      const component = componentsByInspection.get(inspection.id)
      if (component && assetModel) {
        const componentModel = assetModel.data.components.get(component.id)
        if (componentModel) {
          map.set(inspection.id, componentModel)
        }
      }
    }
    return map
  }, [inspections, componentsByInspection, assetModel])

  const headInspectionIds = useMemo(() => {
    const ids = new Set<string>()
    for (const [inspId, model] of componentModelsByInspection.entries()) {
      if (
        model.type === GeckoComponentModelType.EllipsoidalHead ||
        model.type === GeckoComponentModelType.HemisphericalHead
      ) {
        ids.add(inspId)
      }
    }
    return ids
  }, [componentModelsByInspection])

  // Group markup layers by inspection
  const markupLayersByInspection = useMemo(() => {
    const map = new Map<string, readonly GeckoMarkupLayer[]>()

    for (const inspection of inspections) {
      const layers = markupLayers.filter(
        (layer) => layer.tags.inspection === inspection.id
      )
      map.set(inspection.id, layers)
    }

    return map as ReadonlyMap<string, readonly GeckoMarkupLayer[]>
  }, [inspections, markupLayers])

  // Select the most eligible CDF for each inspection
  const relevantThicknessCDFsByInspection = useMemo(() => {
    const map = new Map<string, GeckoComponentDataFrame>()

    for (const inspection of inspections) {
      if (inspection.type !== RolodexEntryType.INSPECTION) {
        continue
      }

      const component = componentsByInspection.get(inspection.id)
      if (!component || !componentDataFrames || !componentDatasets) {
        continue
      }

      // Find the most eligible CDFs for this inspection/component pair
      const mostEligibleCDFs = componentDataFrames
        .filter(
          (cdf) =>
            cdf.tags.component === component.id &&
            cdf.tags.inspection === inspection.id &&
            // Don't default to BinStandardDeviation or AScansPerBin
            cdf.data.params.view !== GeckoView.BinStandardDeviation &&
            cdf.data.params.view !== GeckoView.AScansPerBin
        )
        .sort(GeckoComponentDataFrame.GetDefaultSort(componentDatasets))

      const cdf = mostEligibleCDFs.at(0)
      if (cdf) {
        map.set(inspection.id, cdf)
      }
    }

    return map
  }, [
    inspections,
    componentsByInspection,
    componentDataFrames,
    componentDatasets,
  ])

  const nominalsByInspection = useMemo(() => {
    const nominalsPerInspection: Map<
      string,
      GeckoMeasurement | GeckoMeasurementRange
    > = new Map()

    componentModelsByInspection.entries().forEach(([inspId, model]) => {
      if ('wallNominalThickness' in model && model.wallNominalThickness) {
        nominalsPerInspection.set(inspId, model.wallNominalThickness)
      } else if ('courses' in model && model.courses instanceof Map) {
        const courseNominals = [...model.courses.values()]
          .map((course) =>
            'wallNominalThickness' in course
              ? course.wallNominalThickness
              : undefined
          )
          .filter(
            (thickness): thickness is GeckoMeasurement =>
              thickness !== undefined
          )

        const range = GeckoMeasurementRange.CreateFromStartEnd(
          GeckoMeasurement.Min(...courseNominals),
          GeckoMeasurement.Max(...courseNominals)
        )

        nominalsPerInspection.set(inspId, range)
      }
    })

    return nominalsPerInspection as ReadonlyMap<
      string,
      GeckoMeasurement | GeckoMeasurementRange
    >
  }, [componentModelsByInspection])

  // Asset visualization
  const {
    indicationsVizRef,
    drawingVizRef,
    baseSceneConfig,
    heatmapSceneConfig,
    noHeatmapSceneConfig,
  } = useAssetVizState()

  // Get indication sets for all inspections
  const { indicationSets: _indicationSets } = useIndicationSets(
    { inspection: inspections.map((i) => i.id) },
    undefined,
    false
  )

  // Create map of inspection id to raut indication set
  const indicationSetsByInspection = useMemo(() => {
    const map = new Map<string, (typeof _indicationSets)[0]>()
    const rautIndicationSets = _indicationSets.filter(
      (is) => is.indicationSetType === 'raut_report'
    )

    for (const indicationSet of rautIndicationSets) {
      const inspectionId = indicationSet.tags.inspection
      if (inspectionId && inspections.some((i) => i.id === inspectionId)) {
        map.set(inspectionId, indicationSet)
      }
    }

    return map
  }, [_indicationSets, inspections])

  // Create map of inspection id to weld indication set
  const weldIndicationSetsByInspection = useMemo(() => {
    const map = new Map<string, (typeof _indicationSets)[0]>()
    const weldIndicationSets = _indicationSets.filter(
      (is) => is.indicationSetType === 'weld_report'
    )

    for (const indicationSet of weldIndicationSets) {
      const inspectionId = indicationSet.tags.inspection
      if (inspectionId && inspections.some((i) => i.id === inspectionId)) {
        map.set(inspectionId, indicationSet)
      }
    }

    return map
  }, [_indicationSets, inspections])

  const { prePopulatedData, hasCantileverData } =
    useCantileverReportData(inspections)

  const indicationSetIds = useMemo(
    () => [...indicationSetsByInspection.values().map((is) => is.id)],
    [indicationSetsByInspection]
  )

  const {
    indicationsByInspectionId,
    flatIndications,
    isLoading: isLoadingIndications,
  } = useIndications(indicationSetIds, inspectionIds ?? [])

  const isLoading =
    isLoadingMetadata ||
    isLoadingInspections ||
    isLoadingMarkupLayers ||
    isLoadingAssetModels ||
    isLoadingComponentDatasets ||
    isLoadingIndications

  useEffect(() => {
    if (!isLoading && inspections.length > 0) {
      const currCloudFormData = getSelectedFormData(
        metadataEntryId,
        metadataEntries
      )

      if (
        isEmpty(currCloudFormData?.mainInfo) &&
        isEmpty(currCloudFormData?.summaryPageData)
      ) {
        const unitId = inspections[0]?.tags.unit
        if (!unitId) return

        const regularInspectionIds = inspections
          .filter((i) => i.type === RolodexEntryType.INSPECTION)
          .map((i) => i.id)

        createInitialEntry({
          unitId,
          reportTemplateId: ReportTemplateId.ADVANCED_SERVICES,
          inspectionIds: regularInspectionIds,
          visualInspectionIds: [],
          prePopulatedData: hasCantileverData ? prePopulatedData : undefined,
        })
      }
    }
  }, [
    metadataEntries.length,
    hasCantileverData,
    prePopulatedData,
    createInitialEntry,
    isLoading,
    inspections,
    metadataEntries,
    metadataEntryId,
  ])

  // Form data management
  const {
    formData,
    updateFormData,
    handleMainInfoFinish,
    handleCoverPageFinish,
    handleSummaryPageFinish,
    saveSummaryPageSilent,
    saveMainInfoSilent,
    saveCoverPageSilent,
    saveCalibrationSilent,
    handleCalibrationFinish,
    handleClientProvidedImagesFinish,
    saveClientProvidedImagesSilent,
    handleWeldScanDataFinish,
    handleAssetDrawingsFinish,
    handleSaveIndicationGraph,
    handleDeleteIndicationGraph,
    handleSaveWeldIndicationGraph,
    handleDeleteWeldIndicationGraph,
    handleSignoffFinish,
  } = useFormData(
    metadataEntryId,
    metadataEntries,
    isLoading,
    refreshMetadataEntry
  )

  const weldIndicationSetIds = useMemo(
    () => [...weldIndicationSetsByInspection.values().map((is) => is.id)],
    [weldIndicationSetsByInspection]
  )

  const {
    weldIndicationsByInspectionId,
    flatWeldIndications,
    isLoading: isLoadingWeldIndications,
  } = useWeldIndications(weldIndicationSetIds, inspectionIds ?? [])

  // Fetch rendered visualization URLs for indications
  // This must be called before useReportNavigation to provide the count for completion status
  const {
    indicationsWithImages,
    renderedImageUrls,
    isLoading: isLoadingVisualizationUrls,
  } = useIndicationsWithImages(
    flatIndications,
    formData.savedIndicationGraphs,
    metadataEntryId
  )

  // Navigation
  // When still loading visualization URLs, use -1 to indicate loading state
  // The completion status will show as indeterminate until data is loaded
  const { activePage, navigateToPage, returnToMenu, completionStatus } =
    useReportNavigation({
      formData,
      indicationImages: {
        totalIndications: flatIndications.length,
        capturedScreenshots: isLoadingVisualizationUrls
          ? -1
          : indicationsWithImages.length,
      },
      sortedIndications: flatIndications,
      report: rautReportForSPA,
      inspections,
      headInspectionIds,
    })

  // Additional hooks
  const { assetDrawings } = useAssetDrawings(
    metadataEntryId,
    inspectionIds ?? []
  )
  const { images: clientImages } = useClientImages(metadataEntryId, formData)
  const calibrationImage = useCalibrationImage(
    metadataEntryId,
    formData.calibrationData
  )

  // Lock management
  const {
    isLocked,
    startedSignoff,
    lastLockTimestamp,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,
  } = useLockManagement({
    metadataEntryId,
    formData,
    updateFormData,
    refreshMetadataEntry,
  })

  const lockState = { isLocked, startedSignoff, lastLockTimestamp }

  // Generate PDF download filename
  // Format: Job ID_Customer_Site_Asset_Inspection Service_Inspection End Date_Revision
  const downloadFilename = useMemo(() => {
    const jobId = formData.coverPageData.geckoJobId || ''
    const customer = formData.mainInfo.customerName || ''
    const site = formData.mainInfo.location || ''
    const asset = formData.mainInfo.asset || ''
    const inspectionService = inspections
      .map((i) => i.data.inspection_type?.toUpperCase())
      .filter((type, index, arr) => type && arr.indexOf(type) === index)
      .join('-')
    const endDate = formData.coverPageData.endDate
      ? dayjs(formData.coverPageData.endDate).format('DD-MMM-YYYY')
      : ''
    const revision = `Rev.${formData.revisionMetadata.revisionNumber}`

    return [jobId, customer, site, asset, inspectionService, endDate, revision]
      .filter(Boolean)
      .join('_')
  }, [formData, inspections])

  // PDF management
  const {
    latestDraftTimestamp,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    forceRefresh: refreshPdfState,
  } = usePdfManagement({
    metadataEntryId,
    formData,
    report: rautReportForSPA,
    downloadFilename,
  })

  const pdfState = {
    hasPublishedPdf: !!formData.publishedPdfPath,
    latestDraftTimestamp,
    publishedPdfPath: formData.publishedPdfPath,
  }

  const { weldIndicationsWithImages, renderedWeldImageUrls } =
    useWeldIndicationsWithImages(
      flatWeldIndications,
      formData.savedWeldIndicationGraphs,
      metadataEntryId
    )

  const scanCount = useScanCount(markupLayersByInspection, inspections)
  const totalUT = useTotalUT(inspectionIds ?? [])
  const scanArea = useScanAreaRatio(
    markupLayersByInspection,
    componentModelsByInspection
  )

  const majorDamageRating = useMemo(() => {
    const draNumExtract = (dra: GeckoDRA) =>
      parseInt(dra.substring(dra.length - 1))

    return flatIndications
      .sort(
        (a, b) =>
          draNumExtract(b.customInfo.dra_rating) -
          draNumExtract(a.customInfo.dra_rating)
      )
      .at(0)?.customInfo.sub_rating
  }, [flatIndications])

  const uniqueScanTypes = useMemo(() => {
    const scanTypes = new Set<string>()
    inspections.forEach((inspection) => {
      scanTypes.add(inspection.data.inspection_type ?? '')
    })
    return Array.from(scanTypes)
  }, [inspections])

  const uniqueScanTypesString = useMemo(() => {
    return uniqueScanTypes
      .map((type) => TYPE_TO_METHOD_TITLE[type as GeckoInspectionType])
      .join(', ')
  }, [uniqueScanTypes])

  const hasPhasedArrayInspection = useMemo(() => {
    return inspections.some(
      (inspection) =>
        inspection.data.inspection_type === GeckoSource.PhasedArray.id
    )
  }, [inspections])

  //TODO: Implement these things actually :(
  const selectedRevisionData = useMemo(() => formData, [formData])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadRevisionData = useCallback(async (revisionId: string) => {}, [])
  const copyRevisionContent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (sections: SectionSelection) => {},
    []
  )
  const hasPublishedRevisions = useMemo(() => false, [])
  const canRevertToCantilever = useMemo(() => false, [])
  const revertFormToCantilever = useCallback(async () => {}, [])

  // Report submission
  // Use first inspection for submission
  const {
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
  } = useReportSubmission({
    metadataEntryId,
    formData,
    onUpdateFormData: updateFormData,
    onPublishRevision: async () => {
      await publishRevision()
    },
    flatIndications,
    screenshotUrls: new Map(Object.entries(renderedImageUrls)),
    assetDrawings,
    currentRevisionNumber,
    report: rautReportForSPA,
    getSignedUrlForPath: () => undefined, // TODO: implement
    publishLatestDraftPDF,
  })

  const componentModels = useMemo(() => {
    return Array.from(componentModelsByInspection.values())
  }, [componentModelsByInspection])

  const { diameter: assetDiameter, length: assetLength } = useMemo(
    () => computeAssetDimensions(assetModel, componentModels),
    [assetModel, componentModels]
  )

  const pageSaveHandlersRef = useRef(new Map<string, () => Promise<void>>())

  const registerPageSaveHandler = useCallback(
    (pageId: string, handler?: (() => Promise<void>) | null) => {
      if (!handler) {
        pageSaveHandlersRef.current.delete(pageId)
        return
      }

      pageSaveHandlersRef.current.set(pageId, handler)
    },
    []
  )

  const savePageBeforeNavigation = useCallback(async (page: string | null) => {
    if (!page) return

    const handler = pageSaveHandlersRef.current.get(page)
    if (handler) {
      await handler()
    }
  }, [])

  const contextValue: RautReportContextValue = {
    // Base context values
    activePage,
    report: rautReportForSPA,
    navigateToPage,
    returnToMenu,
    savePageBeforeNavigation,
    completionStatus,

    // Form Data
    formData,
    updateFormData,
    handleSignoffFinish,
    revertFormToCantilever,
    handleMainInfoFinish,
    handleCoverPageFinish,
    handleSummaryPageFinish,
    saveSummaryPageSilent,
    saveMainInfoSilent,
    saveCoverPageSilent,
    saveCalibrationSilent,
    handleCalibrationFinish,
    handleClientProvidedImagesFinish,
    saveClientProvidedImagesSilent,
    handleWeldScanDataFinish,
    handleAssetDrawingsFinish,
    handleSaveIndicationGraph,
    handleDeleteIndicationGraph,
    handleSaveWeldIndicationGraph,
    handleDeleteWeldIndicationGraph,
    registerPageSaveHandler,

    // Metadata
    metadataEntryId,
    isLoading,
    currentRevisionNumber,
    refreshMetadataEntry,

    // Submissions
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,

    // Revisions
    revisions,
    selectedRevisionData,
    loadRevisionData,
    copyRevisionContent,
    hasPublishedRevisions,
    canRevertToCantilever,
    createNewRevision,
    isCurrentRevisionPublished,

    // Visualization
    assetVizRefs: {
      indicationsVizRef,
      drawingVizRef,
    },
    sceneConfigs: {
      baseSceneConfig,
      heatmapSceneConfig,
      noHeatmapSceneConfig,
    },

    // RAUT-specific data
    flatIndications,
    indicationsByInspectionId,
    indicationsWithImages,
    renderedImageUrls,
    assetDrawings,
    calibrationImage,
    clientImages,
    hasCantileverData,
    prePopulatedData: {},
    inspections,
    componentsByInspection,
    assetModel,
    componentModelsByInspection,
    componentModels,
    relevantThicknessCDFsByInspection,
    markupLayersByInspection,
    // Lock Management
    lockState,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,

    // PDF Management
    pdfState,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    refreshPdfState,
    scanCount,
    scanArea,
    totalUT,
    majorDamageRating,
    uniqueScanTypes,
    uniqueScanTypesString,

    // Computed asset dimensions
    assetDiameter,
    assetLength,
    nominalsByInspection,
    unit,
    componentDefaults,

    // Weld Indications
    weldIndicationsByInspectionId,
    flatWeldIndications,
    weldIndicationsWithImages,
    renderedWeldImageUrls,
    isLoadingWeldIndications,
    hasWeldIndications: flatWeldIndications.length > 0,
    hasPhasedArrayInspection,
  }

  return (
    <RautReportContext.Provider value={contextValue}>
      {children}
    </RautReportContext.Provider>
  )
}

export const useRautReportOps = (): RautReportContextValue => {
  const context = React.useContext(RautReportContext)
  if (!context) {
    throw new Error('useRautReportOps must be used within RautReportProvider')
  }
  return context
}
